import React, { useEffect, useState, useCallback } from 'react';
import PrivateLayout from '../../../layouts/PrivateLayout/PrivateLayout';
import { Button, Card, Datepicker, Dropdown, TextInput } from 'flowbite-react';
import { FaEllipsisH, FaSearch } from 'react-icons/fa';
import filterIcon from '../../../assets/filterIcon.png';
import downloadIcon from '../../../assets/document-download.png';
import CustomTable from '../../../components/Table/CustomTable';
import client from '../../../api/client';
import { ColumnChargeback, ChargebackData } from '../../../types/TablesTypes/chargeBack';
import { numberWithCommas } from '../../../utils/formatNumber';
import StatusView from '../../../components/StatusView/StatusView';
import moment from 'moment';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { exportToCSV } from '../../../utils/exportToCSV';
import { exportToXLSX } from '../../../utils/exportToXLSX';

interface ChargebackTemplateProps {
  title?: string,
  status?: string
}

const ChargebackTemplate = ({ title, status }: ChargebackTemplateProps) => {
  const [chargebacks, setChargebacks] = useState<any>();
  const [chargebackOverview, setChargebackOverview] = useState({
    overview: {
      thresholdPercent: 0.0,
      chargebackValue: 0.0,
      chargebackCount: 0
    },
    holdingBalances: []
  });
  const { currency } = useCurrency()
  const options = ["Pending", "Approved"];
  const [filters, setFilters] = useState({
    status: status ?? "",
    reference: "",
    email: "",
    fromDate: "",
    toDate: "",
    export: false
  });
  const [tableRow, setTableRow] = useState<any[]>([]);
  const [selectedOption, setSelectedOption] = useState<any>(options[0]);
  const [loading, setLoading] = useState<boolean>(true);


  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      await getChargeBacks();
      await getChargeBackOverview();
      setLoading(false)
    };
    fetchData();
  }, [filters]);

  const getChargeBacks = async () => {
    try {
      const response = await client.get(`/subsidiary/dashboard/chargeback/all`, {
        params: filters
      });
      setChargebacks(response.data.data.items);
    } catch (err: any) {
      if (err?.response?.data?.message === 'Please login again') {
        sessionStorage.clear();
        window.location.href = '/';
      } else {
        console.error('error getting data', err);
      }
    }
  };

  const getChargeBackOverview = async () => {
    try {
      const response = await client.get('/subsidiary/dashboard/chargeback/overview');
      console.log(response);
      setChargebackOverview(response.data.data);
    } catch (err: any) {
      if (err?.response?.data?.message === 'Please login again') {
        sessionStorage.clear();
        window.location.href = '/';
      } else {
        console.error('error getting data', err);
      }
    }
  };

  const handleFilterChange = useCallback(debounce((key: string, value: any) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value
    }));
  }, 500), []);

  const dataPaymentLinks = () => {
    const tempArr: ChargebackData[] = [];
    chargebacks?.data?.items
      ?.slice(0)
      .reverse()
      .forEach((balance: any, index: number) => {
        tempArr.push({
          date: moment(balance?.dueDate).format("MMMM Do YYYY, h:mm a"),
          amount: `${balance?.currency ?? "NGN"} ${numberWithCommas(balance?.amount)}`,
          status: (
            <StatusView
              status={balance?.status}
              green="Won"
              red="Lost | Accepted"
              orange="Pending"
              blue="New"
              indigo="Awaiting Response"
            />
          ),
          balance: balance?.balance
            ? `${balance?.currency ?? "NGN"} ${numberWithCommas(balance?.balance)}`
            : "Nil",
          id: balance?.id || 'Nil',
          reference: balance?.transactionReference || 'Nil',
          action: <FaEllipsisH />,
        });
      });
    return tempArr;
  };

  useEffect(() => {
    setTableRow(dataPaymentLinks());
  }, [chargebacks?.items]);

  const currentDate = new Date().toLocaleDateString('en-CA')

  return (
    <PrivateLayout>
      <div className='my-10 mx-6'>
        <div className='flex items-center gap-3'>
          <p className='font-semibold text-xl'>{title}</p>
          <div className='inline-block border py-3 px-6 rounded-full'>
            <Dropdown label="Overview" inline arrowIcon className='rounded-xl'>
              <div className='w-48 px-3 py-4'>
                <p className='text-sm font-semibold'>Chargebacks</p>
                <Dropdown.Item>
                  <div className='inline-block text-md border py-3 px-4 rounded-full w-full'>
                    Overview
                  </div>
                </Dropdown.Item>
                <Dropdown.Item>
                  <div className='text-md border py-3 px-4 rounded-full w-full'>
                    Holding Balance
                  </div>
                </Dropdown.Item>
              </div>
            </Dropdown>
          </div>
        </div>
        <div className='flex flex-wrap my-6'>
          <div className='flex items-center gap-3'>
            <Datepicker onSelectedDateChanged={(date: any) => handleFilterChange('fromDate', date)} /> to <Datepicker onSelectedDateChanged={(date: any) => handleFilterChange('toDate', date)} />
            <div className='bg-custom-button-green py-2 px-3 rounded-sm'>
              <Dropdown
                label={
                  <div className='flex items-center gap-2 text-sm '>
                    <div>
                      <img src={filterIcon} alt='Filter' />
                    </div>
                    <p>{selectedOption}</p>
                  </div>
                }
                inline
                dismissOnClick={true}
                arrowIcon={false}
              >
                {options.map(option => (
                  <Dropdown.Item key={option} onClick={() => {
                    setSelectedOption(option);
                    handleFilterChange('status', option);
                  }}>{option}</Dropdown.Item>
                ))}
              </Dropdown>
            </div>
          </div>
          <div className='flex items-center gap-3 mt-2 lg:mt-0 lg:ml-auto '>
            <div className='items-center'>
              <TextInput
                icon={FaSearch}
                name='search'
                placeholder='Search'
                onChange={(e) => handleFilterChange('reference', e.target.value)}
              />
            </div>
            <Dropdown label={
              <Button className='rounded-md bg-black'>
                <div className='flex gap-2'>
                  <img src={downloadIcon} alt="Download" />
                  <span>Export</span>
                </div>
              </Button>
            } inline arrowIcon={false}>
              <Dropdown.Item onClick={() => exportToCSV(chargebacks?.items, `chargebacks-${currentDate}.csv`)}>Export as .csv</Dropdown.Item>
              <Dropdown.Item onClick={() => exportToXLSX(chargebacks?.items, `chargebacks-${currentDate}.xlsx`)}>Export as .xls</Dropdown.Item>
            </Dropdown>

          </div>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 my-6'>
          <div>
            <Card>
              <p>Remaining of your threshold</p>
              <div className='flex gap-2 pb-10'>
                <p>{currency}</p>
                <p className='text-xl font-bold'>{chargebackOverview.overview.thresholdPercent}</p>
              </div>
            </Card>
          </div>
          <div>
            <Card>
              <p>Chargeback value</p>
              <div className='flex gap-2 pb-10'>
                <p>{currency}</p>
                <p className='text-xl font-bold'>{chargebackOverview.overview.chargebackValue}</p>
              </div>
            </Card>
          </div>
          <div>
            <Card>
              <p>Chargeback count</p>
              <div className='flex gap-2 pb-10'>
                <p className='text-xl font-bold'>{chargebackOverview.overview.chargebackCount}</p>
              </div>
            </Card>
          </div>
        </div>
        <div>
          <CustomTable data={tableRow ? tableRow : []} headings={ColumnChargeback} loading={loading} emptyMessage={`You do not have any chargeback in ${currency}`} />
        </div>
      </div>
    </PrivateLayout>
  );
};

export default ChargebackTemplate;
